import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ViewStudent from "../ViewStudent/ViewStudent";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Student.css";

const API_URL = "https://aadyaacademy.org/api";

function Student() {
  const [studentList, setStudentList] = useState([]);
  const [filteredStudent, setFilteredStudent] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingStudent, setViewingStudent] = useState(null);

  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    const teacherPhone = localStorage.getItem("userPhone");

    if (!teacherPhone) {
      toast.error("Teacher phone number is not available.");
      setLoading(false);
      return;
    }

    try {
      const [studentsResponse, sectionsResponse, classesResponse] = await Promise.all([
        axios.get(`${API_URL}/students/teacherPhone`, { params: { teacherPhone } }),
        axios.get(`${API_URL}/get-section/${teacherPhone}`),
        axios.get(`${API_URL}/get-class/${teacherPhone}`)
      ]);

      if (studentsResponse.data.error) {
        toast.error(studentsResponse.data.error);
        setLoading(false);
        return;
      }
      if (sectionsResponse.data.error) {
        toast.error(sectionsResponse.data.error);
        setLoading(false);
        return;
      }
      if (classesResponse.data.error) {
        toast.error(classesResponse.data.error);
        setLoading(false);
        return;
      }

      let students = studentsResponse.data || [];
      students.sort((a, b) =>
        a.student_name.toLowerCase().localeCompare(b.student_name.toLowerCase())
      );

      const uniqueClasses = [
        ...new Set(classesResponse.data.class_teacher_for.map(item => item.class))
      ].sort();

      setStudentList(students);
      setSections(sectionsResponse.data.section || []);
      setClasses(uniqueClasses);
    } catch (error) {
      toast.error(error.response?.data?.error || "Error fetching data. Please try again.");
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDateToDisplay = (date) => {
    if (!date) return "N/A";
    try {
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    } catch {
      return "Invalid Date";
    }
  };

  useEffect(() => {
    let filtered = [...studentList];

    if (searchTerm) {
      const searchValue = searchTerm.toLowerCase();
      filtered = filtered.filter((student) => {
        return (
          student.student_id?.toString().includes(searchValue) ||
          student.student_name?.toLowerCase().includes(searchValue) ||
          student.class?.toLowerCase().includes(searchValue) ||
          student.section?.toLowerCase().includes(searchValue) ||
          student.gender?.toLowerCase().includes(searchValue) ||
          (student.dob && new Date(student.dob).toISOString().split("T")[0].includes(searchValue))
        );
      });
    }

    if (classFilter) {
      filtered = filtered.filter((student) => student.class === classFilter);
    }

    if (sectionFilter) {
      filtered = filtered.filter((student) => student.section === sectionFilter);
    }

    if (selectedDate) {
      const [day, month, year] = selectedDate.split("/");
      const formattedSelectedDate = `${year}-${month}-${day}`;
      filtered = filtered.filter((student) => {
        if (!student.dob) return false;
        const dobFormatted = new Date(student.dob).toISOString().split("T")[0];
        return dobFormatted === formattedSelectedDate;
      });
    }

    setFilteredStudent(filtered);
  }, [searchTerm, classFilter, sectionFilter, selectedDate, studentList]);

  const handleView = (student) => {
    setViewingStudent(student);
  };

  const handleDateChange = (e) => {
    const rawDate = e.target.value;
    if (!rawDate) {
      setSelectedDate("");
      return;
    }
    const [year, month, day] = rawDate.split("-");
    setSelectedDate(`${day}/${month}/${year}`);
  };

  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${year}-${month}-${day}`;
  };

  if (viewingStudent) {
    return <ViewStudent student={viewingStudent} onClose={() => setViewingStudent(null)} />;
  }

  return (
    <div className="student">
      <div className="student-table">
        <div className="table-header">
          <div className="table-header-top">
            <h2>Student List: {filteredStudent.length}</h2>
          </div>
          <div className="table-header-btm">
            <div className="filter-container">
              <input
                type="search"
                className="search-input"
                placeholder="Search by ID, Name, Class, Section, Gender, or DOB"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
              <select
                className="class-filter"
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <option value="">All Classes</option>
                {classes.map((cls, index) => (
                  <option key={index} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>
              <select
                className="section-filter"
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
              >
                <option value="">All Sections</option>
                {sections.map((section, index) => (
                  <option key={index} value={section}>
                    {section}
                  </option>
                ))}
              </select>
              
            </div>
          </div>
        </div>

        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th className="thead-content">ID</th>
                <th className="thead-content">Name</th>
                <th className="thead-content">Class</th>
                <th className="thead-content">Section</th>
                <th className="thead-content">Gender</th>
                <th className="thead-content">DOB</th>
                <th className="thead-content">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : filteredStudent.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No students found
                  </td>
                </tr>
              ) : (
                filteredStudent.map((student) => (
                  <tr key={student.student_id}>
                    <td className="tbody-content" data-label="ID">{student.student_id || "N/A"}</td>
                    <td className="tbody-content" data-label="Name">{student.student_name || "N/A"}</td>
                    <td className="tbody-content" data-label="Class">{student.class || "N/A"}</td>
                    <td className="tbody-content" data-label="Section">{student.section || "N/A"}</td>
                    <td className="tbody-content" data-label="Gender">{student.gender || "N/A"}</td>
                    <td className="tbody-content" data-label="DOB">
                      {student.dob && !isNaN(new Date(student.dob))
                        ? formatDateToDisplay(new Date(student.dob).toISOString().split("T")[0])
                        : "Invalid Date"}
                    </td>
                    <td className="actions" data-label="Actions">
                      <button
                        className="view-btn"
                        onClick={() => handleView(student)}
                        onTouchStart={() => handleView(student)} // Added for touch support
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
    </div>
  );
}

export default Student;